import React, { useState } from 'react';
import { func } from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';

import PasswordRequirements from '../../components/PasswordRequirements';

import clsx from 'clsx';
import classes from './AuthCore.module.css';

const ValidationSchema = Yup.object().shape({
    code: Yup.string()
        .required('Verification code required - check your email inbox'),
    password: Yup.string()
        .min(6)
        .max(99)
        .matches(
            // eslint-disable-next-line no-useless-escape
            /^(?=[0-9a-zA-Z\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]{6,}$)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=[^0-9]*[0-9])(?=[^\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]*[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]).*/,
            'Password does not match required format.'
        )
        .required('Password required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Password confirmation required')
});

const ResetPasswordForm = props => {
    const formik = useFormik({
        initialValues: {
            code:            '',
            password:        '',
            confirmPassword: ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         props.onSubmit
    });

    const [anchorEl, setAnchorEl] = useState();
    const [popperOpen, setPopperOpen] = useState(false);

    return (
        <React.Fragment>
            <form
                onSubmit={formik.handleSubmit}
                className={classes.form}
            >
                <FormControl className={classes.formControl}>
                    <InputLabel
                        htmlFor="code"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.code) && Boolean(formik.touched.code)}
                    >
                        Verification Code
                    </InputLabel>
                    <Input
                        id="code"
                        name="code"
                        value={formik.values.code}
                        onChange={formik.handleChange}
                        classes={{
                            error:     classes.cssErrorUnderline,
                            underline: classes.cssUnderline,
                            root:      classes.input
                        }}
                        error={Boolean(formik.errors.code) && Boolean(formik.touched.code)}
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                    />
                    {formik.touched.code &&
                    formik.errors.code && <FormHelperText className={classes.error}>{formik.errors.code}</FormHelperText>}
                </FormControl>
                <FormControl className={classes.formControl}>
                    <InputLabel
                        htmlFor="password"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                    >
                        New Password
                    </InputLabel>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        classes={{
                            error:     classes.cssErrorUnderline,
                            underline: classes.cssUnderline,
                            root:      classes.input
                        }}
                        error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                        endAdornment={
                            <InputAdornment
                                onClick={event => {
                                    setPopperOpen(!popperOpen);
                                    setAnchorEl(event.currentTarget);
                                }}
                                position="end"
                            >
                                <i className="fas fa-question-circle" />
                            </InputAdornment>
                        }
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                    />
                    {formik.touched.password &&
                    formik.errors.password && <FormHelperText className={classes.error}>{formik.errors.password}</FormHelperText>}
                </FormControl>
                <PasswordRequirements
                    open={popperOpen}
                    anchorEl={anchorEl}
                    placement="left"
                />
                <FormControl className={classes.formControl}>
                    <InputLabel
                        htmlFor="confirmPassword"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.confirmPassword) && Boolean(formik.touched.confirmPassword)}
                    >
                        Confirm Password
                    </InputLabel>
                    <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        classes={{
                            error:     classes.cssErrorUnderline,
                            underline: classes.cssUnderline,
                            root:      classes.input
                        }}
                        error={Boolean(formik.errors.confirmPassword) && Boolean(formik.touched.confirmPassword)}
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                    />
                    {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && <FormHelperText className={classes.error}>{formik.errors.confirmPassword}</FormHelperText>}
                </FormControl>
                <div className={clsx(classes.buttonContainer, classes.forgotPasswordButtonContainer)}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={formik.isSubmitting}
                        className={classes.primaryFullWidth}
                    >
                        Reset Password
                    </Button>
                </div>
            </form>
            <p>
                <b>Please note:</b> The verification code email will be sent from ‘no-reply@wennsoft.com’ email address. If you do not receive the email, check your junk mail, and add the contact to your safe senders list. Still having trouble finding your code, contact your IT department to ensure the ‘no-reply@wennsoft.com’ email address is not being blocked.
            </p>
        </React.Fragment>
    );
};

ResetPasswordForm.defaultProps = {
    onSubmit: () => {}
};

ResetPasswordForm.propTypes = {
    onSubmit: func
};

export default ResetPasswordForm;
